/**
 * OtaDebugLogger
 *
 * Bridge JS → logcat nativo (Android) via OtaDebugPlugin.
 *
 * <p><b>Motivação:</b> Capacitor 7 em release build strippa (ou redireciona
 * pra channel invisível) os {@code console.log} do WebView. Em Tab A7 com
 * Device Owner + kiosk, isso significa que TODO o flow do OtaService fica
 * como caixa-preta — impossível debugar OTA travado sem instrumentação
 * nativa.</p>
 *
 * <p><b>Uso:</b></p>
 * <pre>
 * import { otaLog } from './OtaDebugLogger';
 *
 * otaLog.info('checkForUpdate START', { version: '1.0.75' });
 * otaLog.warn('Update guard tripped', { reason: 'pending_install' });
 * otaLog.error('Install failed', err);
 * </pre>
 *
 * <p><b>Filtro logcat:</b></p>
 * <pre>
 * adb logcat -v time "OtaDebug:V" "*:S"
 * </pre>
 *
 * <p>Em plataformas não-Capacitor (browser, Tizen, webOS) ou se o plugin não
 * estiver disponível, faz fallback pra {@code console.log/warn/error} normal.
 * Zero impacto em platforms onde o plugin não existe.</p>
 *
 * @since v1.0.77 — 2026-05-29
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface OtaDebugPlugin {
  log: (options: {
    level?: LogLevel;
    tag?: string;
    msg: string;
  }) => Promise<{ logged: boolean }>;
}

interface CapacitorBridge {
  Plugins?: {
    OtaDebug?: OtaDebugPlugin;
  };
}

interface WindowWithCapacitor extends Window {
  Capacitor?: CapacitorBridge;
}

/**
 * Resolve uma única vez no module-load. Cache evita lookup repetido.
 */
const getNativePlugin = (): OtaDebugPlugin | null => {
  if (typeof window === 'undefined') return null;
  const cap = (window as WindowWithCapacitor).Capacitor;
  return cap?.Plugins?.OtaDebug ?? null;
};

const formatMsg = (msg: string, ctx?: Record<string, unknown>): string => {
  if (!ctx) return msg;
  try {
    const parts = Object.entries(ctx)
      .map(
        ([k, v]) =>
          `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`,
      )
      .join(' ');
    return `${msg} | ${parts}`;
  } catch {
    return msg;
  }
};

const dispatch = (
  level: LogLevel,
  msg: string,
  ctx?: Record<string, unknown>,
): void => {
  const formatted = formatMsg(msg, ctx);
  const tag = 'OtaDebug';

  // Sempre escreve no console também (dev mode + outras plataformas)
  const consoleFn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.log;
  consoleFn(`[${tag}] ${formatted}`);

  // Em Android Capacitor: dispatch pro plugin nativo pra aparecer no logcat
  const plugin = getNativePlugin();
  if (plugin) {
    plugin.log({ level, tag, msg: formatted }).catch(() => {
      // Plugin falhou — não escala. Console.log já foi feito acima.
    });
  }
};

export const otaLog = {
  info: (msg: string, ctx?: Record<string, unknown>) =>
    dispatch('info', msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) =>
    dispatch('warn', msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) =>
    dispatch('error', msg, ctx),
  debug: (msg: string, ctx?: Record<string, unknown>) =>
    dispatch('debug', msg, ctx),
};
